import type { Pose, TransitionKind } from './types';

/**
 * Transition and motion keyframes for the Web Animations API.
 *
 * Only `opacity` and `transform` are animated (plus `filter` for the optional
 * blur dissolve), so the compositor does the work and the main thread stays
 * free. Each shot layer has two nested elements: the OUTER one carries the
 * transition, the INNER one carries the Ken Burns move, so the two never
 * fight over the same transform.
 */

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

export interface Anim {
  keyframes: Keyframe[];
  options: KeyframeAnimationOptions;
}

/**
 * How the incoming shot appears. A cross-dissolve fades the NEW layer in over
 * the old one, which stays fully opaque underneath: no dip in brightness.
 */
export function enterAnimation(kind: TransitionKind, ms: number): Anim | null {
  if (kind === 'cut' || ms <= 0) return null;
  const base = { duration: ms, easing: EASE, fill: 'both' as const };
  switch (kind) {
    case 'dissolve':
      return { keyframes: [{ opacity: 0 }, { opacity: 1 }], options: base };
    case 'fade-black':
      // Second half only: the outgoing shot has already faded to black.
      return {
        keyframes: [{ opacity: 0 }, { opacity: 1 }],
        options: { ...base, duration: ms / 2, delay: ms / 2, easing: 'ease-out' },
      };
    case 'push':
      return {
        keyframes: [
          { opacity: 0, transform: 'translate3d(7%,0,0)' },
          { opacity: 1, transform: 'translate3d(0,0,0)' },
        ],
        options: { ...base, easing: EASE_OUT },
      };
    case 'zoom':
      return {
        keyframes: [
          { opacity: 0, transform: 'scale(1.06)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        options: { ...base, easing: EASE_OUT },
      };
    case 'blur':
      return {
        keyframes: [
          { opacity: 0, filter: 'blur(14px)' },
          { opacity: 1, filter: 'blur(0px)' },
        ],
        options: base,
      };
  }
}

/** How the outgoing shot leaves while the next one enters. */
export function exitAnimation(kind: TransitionKind, ms: number): Anim | null {
  if (kind === 'cut' || ms <= 0) return null;
  const base = { duration: ms, easing: EASE, fill: 'both' as const };
  switch (kind) {
    case 'dissolve':
    case 'zoom':
      // Stays put underneath; the incoming layer covers it.
      return { keyframes: [{ opacity: 1 }, { opacity: 1 }], options: base };
    case 'fade-black':
      return { keyframes: [{ opacity: 1 }, { opacity: 0 }], options: { ...base, duration: ms / 2, easing: 'ease-in' } };
    case 'push':
      return {
        keyframes: [
          { opacity: 1, transform: 'translate3d(0,0,0)' },
          { opacity: 0.35, transform: 'translate3d(-7%,0,0)' },
        ],
        options: { ...base, easing: EASE_OUT },
      };
    case 'blur':
      return {
        keyframes: [
          { opacity: 1, filter: 'blur(0px)' },
          { opacity: 0, filter: 'blur(10px)' },
        ],
        options: base,
      };
  }
}

const poseCss = (p: Pose) => `translate3d(${p.x.toFixed(3)}%, ${p.y.toFixed(3)}%, 0) scale(${p.scale.toFixed(4)})`;

/** Ken Burns: linear, so motion is continuous through both transitions. */
export function motionAnimation(from: Pose, to: Pose, durationMs: number): Anim {
  return {
    keyframes: [{ transform: poseCss(from) }, { transform: poseCss(to) }],
    options: { duration: durationMs, easing: 'linear', fill: 'both' },
  };
}

/**
 * Every running animation registers here so the player can freeze the whole
 * frame (motion and any in-flight transition) on pause and continue exactly
 * where it stopped on resume. CSS animations cannot do that reliably.
 */
export class AnimationRegistry {
  private anims = new Set<Animation>();
  private paused = false;

  add(anim: Animation) {
    this.anims.add(anim);
    if (this.paused) anim.pause();
    const drop = () => this.anims.delete(anim);
    anim.addEventListener('finish', drop);
    anim.addEventListener('cancel', drop);
    return anim;
  }

  pause() {
    this.paused = true;
    for (const a of this.anims) if (a.playState === 'running') a.pause();
  }

  resume() {
    this.paused = false;
    for (const a of this.anims) if (a.playState === 'paused') a.play();
  }

  get isPaused() {
    return this.paused;
  }
}
